// What the Time Off cards are allowed to say about a balance.
//
// These three numbers used to be derived in three different ways on the same
// screen, so they contradicted each other: "52 Days Remaining" sat beside
// "98% Balance" because remaining was (entitled − taken) while the percentage
// came from the server's `available`. The difference was a day the employee had
// already requested.

/** One row of GET /leaves/balances. The endpoint is untyped (`any[]`). */
export interface BalanceRow {
  entitled?: number | string | null;
  taken?: number | string | null;
  pending?: number | string | null;
  /** entitled + carried_forward + adjusted − taken − pending, computed server-side. */
  available?: number | string | null;
  /** Multi-entity G5: the employment this ledger belongs to (null = single ledger). */
  engagement_id?: string | null;
}

/**
 * Multi-entity G5: the rows of ONE employment's ledger. The primary employment
 * also owns rows not yet stamped with an engagement (its adopted single
 * ledger); any other employment only its own. No employment selected → the
 * single ledger (everyone employed by one company). Pure.
 */
export function rowsForEmployment<T extends BalanceRow>(rows: T[], engagementId: string | null, isPrimary: boolean): T[] {
  if (!engagementId) return rows.filter((r) => !r.engagement_id);
  const own = rows.filter((r) => r.engagement_id === engagementId);
  if (!isPrimary) return own;
  const types = new Set(own.map((r) => (r as { type?: string }).type));
  return [...own, ...rows.filter((r) => !r.engagement_id && !types.has((r as { type?: string }).type))];
}

export interface BalanceView {
  entitled: number;
  taken: number;
  /** Submitted, not yet approved. Already deducted from `remaining`. */
  pending: number;
  /** What the employee may still book. */
  remaining: number;
  /** remaining / entitled, as a whole percent. */
  percent: number;
}

const n = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

/**
 * Derive the cards from one balance row.
 *
 * `remaining` is the SERVER's `available`, never a local subtraction. Computing
 * it here as (entitled − taken) dropped three things at once: pending requests,
 * carry-forward from last year, and manual adjustments. The percentage is
 * derived from the same number so the two cards cannot disagree.
 */
export function deriveBalance(row: BalanceRow | null | undefined): BalanceView {
  const entitled = n(row?.entitled);
  const taken = n(row?.taken);
  const pending = n(row?.pending);
  // A row that predates `available` (or an informational type booked past its
  // soft target) must not render as a negative or a NaN.
  const raw = row?.available === undefined || row?.available === null
    ? entitled - taken - pending
    : n(row.available);
  const remaining = Math.max(0, raw);
  return {
    entitled,
    taken,
    pending,
    remaining,
    percent: entitled > 0 ? Math.round((remaining / entitled) * 100) : 0,
  };
}

/** GET /leaves/my-employments, as far as the ledger choice needs it. */
export interface LeaveEmploymentsView {
  per_employment: boolean;
  employments: Array<{ engagement_id: string; is_primary: boolean }>;
}

/**
 * The rows of the ledger a person BOOKS from — the one Time Off shows and the
 * submission's balance check (leave-service check_leave_balance) charges:
 * someone employed by several companies books from one employment's ledger
 * (`engagementId`, default the primary — what Time Off opens on); everyone
 * else from the single ledger. Employments unknown (the call failed) → the
 * single ledger. NEVER the rows of several ledgers together.
 */
export function bookingLedgerRows<T extends BalanceRow>(
  rows: T[], employments: LeaveEmploymentsView | null | undefined, engagementId?: string,
): T[] {
  const list = employments?.per_employment ? employments.employments : [];
  const chosen = list.find((e) => e.engagement_id === engagementId) ?? list.find((e) => e.is_primary) ?? list[0];
  return rowsForEmployment(rows, chosen ? chosen.engagement_id : null, chosen?.is_primary ?? false);
}

const isAnnual = (r: BalanceRow & { type?: string; type_code?: string }) =>
  String(r.type_code ?? r.type ?? '').toUpperCase() === 'ANNUAL' || r.type === 'vacation';

/**
 * The dashboard headline ("N annual days remaining"): the ANNUAL row of the
 * booking ledger, derived by deriveBalance — the same row and arithmetic as
 * Time Off's Annual card, so the two screens can't disagree
 * (DEF-0mvpyq10c003-513523: the dashboard summed every ledger's ANNUAL rows,
 * 0 + 8 = "8 remaining" beside Time Off's 0 and a refused 3-day request).
 * A tenant with no ANNUAL type: that ledger's accrual types, summed.
 */
export function annualHeadline<T extends BalanceRow & { type?: string; type_code?: string; counts_toward_balance?: boolean }>(
  rows: T[], employments: LeaveEmploymentsView | null | undefined,
): { remaining: number; entitled: number; taken: number } {
  const ledger = bookingLedgerRows(rows, employments);
  const annual = ledger.filter(isAnnual);
  if (annual.length > 0) {
    const v = deriveBalance(annual[annual.length - 1]);  // Time Off keeps the last row of a type
    return { remaining: v.remaining, entitled: v.entitled, taken: v.taken };
  }
  return ledger.filter((r) => r.counts_toward_balance ?? true).map(deriveBalance)
    .reduce((s, v) => ({ remaining: s.remaining + v.remaining, entitled: s.entitled + v.entitled, taken: s.taken + v.taken }),
      { remaining: 0, entitled: 0, taken: 0 });
}
